import { useState } from "react";

import { apenasDigitosCnpj, formatarCnpjEntrada } from "./cnpj-entrada";
import { normalizarDecimalEntrada } from "../../lib/formato/decimal";
import { CategoriaErro, ErroHttp } from "../../lib/http/erros";
import type { ReceitaManualIn } from "../../lib/receitas/tipos";

/*
 * Formulário de lançamento de receita manual (Tela 5 §10.5 — 1ª escrita de money
 * desta tela). Componente controlado por `useState` por campo, espelhando o padrão
 * de `ui/cadastros/FormularioConta`/`FormularioEmpresa`.
 *
 * Contrato BE selado: SÓ 3 campos no corpo (`valor`, `data_prevista`,
 * `cnpj_contraparte`). O `valor` é parseado por `normalizarDecimalEntrada`
 * (string-based, ZERO `Number`/`parseFloat`) → string canônica `"1500.00"`. O
 * CNPJ é digitado COM máscara e enviado como 14 dígitos PUROS (ou `null` se vazio).
 *
 * Componente PURO: não conhece o hook de mutação. O orquestrador injeta `aoLancar`,
 * `enviando`, o `erro` normalizado e o `sucesso`. Acessibilidade: `htmlFor`,
 * `aria-required`, `aria-invalid`, erro de campo com `role="alert"` e foco no
 * primeiro campo inválido; SÓ tokens CPMH (sem estilo inline).
 */

/** Hint EXATO do campo Data prevista — fiel ao motor (texto literal, não derivar). */
const HINT_DATA =
  "Datas futuras ou de hoje entram na projeção de caixa. Datas passadas são " +
  "registradas como receita atrasada não conciliada — ficam salvas, mas não " +
  "somam na projeção.";

interface FormReceitaManualProps {
  /** Dispara o POST com os 3 campos já validados/normalizados. */
  aoLancar: (dados: ReceitaManualIn) => void;
  /** Há um lançamento em andamento? Trava os controles e troca o label. */
  enviando: boolean;
  /** Erro normalizado da mutação (ou `null`/`undefined` se não houve). */
  erro: unknown;
  /** `true` após um 201 — exibe o banner verde de sucesso. */
  sucesso: boolean;
}

/** Erros de campo (borda client-side). `null` = campo válido. */
interface ErrosCampo {
  valor: string | null;
  data: string | null;
  cnpj: string | null;
}

const SEM_ERROS: ErrosCampo = { valor: null, data: null, cnpj: null };

const CLASSE_INPUT =
  "rounded-md border border-platinum bg-snow px-2 py-1 text-off-black disabled:opacity-60";

export function FormReceitaManual({
  aoLancar,
  enviando,
  erro,
  sucesso,
}: FormReceitaManualProps): JSX.Element {
  const [valor, setValor] = useState("");
  const [data, setData] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [erros, setErros] = useState<ErrosCampo>(SEM_ERROS);

  /** Limpa o formulário ao estado inicial (Cancelar e pós-sucesso). */
  function limpar(): void {
    setValor("");
    setData("");
    setCnpj("");
    setErros(SEM_ERROS);
  }

  function submeter(): void {
    const proximos: ErrosCampo = { valor: null, data: null, cnpj: null };

    // Valor: obrigatório, > 0, ≤ 2 casas (parse string-based).
    const valorCanonico = normalizarDecimalEntrada(valor);
    if (valorCanonico === null) {
      // `normalizarDecimalEntrada` rejeita vazio, formato inválido E > 2 casas.
      // Distinguimos "casas a mais" para a mensagem ser acionável.
      proximos.valor = temCasasDemais(valor)
        ? "Use no máximo 2 casas decimais."
        : "Informe um valor maior que zero.";
    } else if (!ehPositivo(valorCanonico)) {
      proximos.valor = "Informe um valor maior que zero.";
    }

    // Data: obrigatória, ISO válida (o input type=date já entrega ISO ou "").
    if (data.trim() === "") {
      proximos.data = "Informe a data prevista.";
    }

    // CNPJ: opcional; se preenchido, exatamente 14 dígitos após remover a máscara.
    const cnpjDigitos = apenasDigitosCnpj(cnpj);
    if (cnpjDigitos !== "" && cnpjDigitos.length !== 14) {
      proximos.cnpj = "CNPJ inválido.";
    }

    setErros(proximos);

    const temErro =
      proximos.valor !== null ||
      proximos.data !== null ||
      proximos.cnpj !== null;
    if (temErro) {
      focarPrimeiroErro(proximos);
      return;
    }

    // `valorCanonico` é não-nulo aqui (sem erro de valor).
    aoLancar({
      valor: valorCanonico as string,
      data_prevista: data,
      cnpj_contraparte: cnpjDigitos === "" ? null : cnpjDigitos,
    });
  }

  function aoCancelar(): void {
    limpar();
  }

  return (
    <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
      <h2 className="font-titulo text-xl text-off-black">Lançar receita manual</h2>

      {sucesso && (
        <p
          role="status"
          className="rounded-md border border-platinum bg-snow px-3 py-2 font-subtitulo text-off-black"
        >
          Receita lançada com sucesso.
        </p>
      )}

      {erro !== null && erro !== undefined && (
        <p role="alert" className="font-corpo text-base text-flame">
          {mensagemAmigavel(erro)}
        </p>
      )}

      <div className="flex flex-col gap-1">
        <label htmlFor="receita-valor" className="font-subtitulo">
          Valor
        </label>
        <div className="flex items-center gap-2">
          <span aria-hidden="true">R$</span>
          <input
            id="receita-valor"
            type="text"
            inputMode="decimal"
            value={valor}
            onChange={(evento) => setValor(evento.target.value)}
            disabled={enviando}
            aria-required="true"
            aria-invalid={erros.valor !== null}
            className={`flex-1 ${CLASSE_INPUT}`}
          />
        </div>
        {erros.valor !== null && (
          <p role="alert" className="font-corpo text-base text-flame">
            {erros.valor}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="receita-data" className="font-subtitulo">
          Data prevista
        </label>
        <input
          id="receita-data"
          type="date"
          value={data}
          onChange={(evento) => setData(evento.target.value)}
          disabled={enviando}
          aria-required="true"
          aria-invalid={erros.data !== null}
          className={CLASSE_INPUT}
        />
        <p className="font-corpo text-sm text-off-black">{HINT_DATA}</p>
        {erros.data !== null && (
          <p role="alert" className="font-corpo text-base text-flame">
            {erros.data}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="receita-cnpj" className="font-subtitulo">
          CNPJ da contraparte (opcional)
        </label>
        <input
          id="receita-cnpj"
          type="text"
          inputMode="numeric"
          value={cnpj}
          onChange={(evento) => setCnpj(formatarCnpjEntrada(evento.target.value))}
          disabled={enviando}
          aria-invalid={erros.cnpj !== null}
          placeholder="00.000.000/0000-00"
          className={CLASSE_INPUT}
        />
        {erros.cnpj !== null && (
          <p role="alert" className="font-corpo text-base text-flame">
            {erros.cnpj}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={aoCancelar}
          disabled={enviando}
          className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submeter}
          disabled={enviando}
          className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
        >
          {enviando ? "Lançando…" : "Lançar receita"}
        </button>
      </div>
    </div>
  );
}

/**
 * `true` sse a entrada de money digitada tem mais de 2 casas decimais — usado para
 * dar a mensagem específica de "casas a mais" quando `normalizarDecimalEntrada`
 * rejeita. STRING-BASED: olha os dígitos após o ÚLTIMO separador decimal (`,` se
 * houver, senão `.`); não usa `Number`/`parseFloat`.
 */
function temCasasDemais(entrada: string): boolean {
  const limpo = entrada.trim();
  const separador = limpo.includes(",") ? "," : ".";
  const indice = limpo.lastIndexOf(separador);
  if (indice === -1) {
    return false;
  }
  const decimais = limpo.slice(indice + 1);
  // Só conta como "casas demais" se o que vem depois forem dígitos (senão é outro
  // tipo de malformação, tratada pela mensagem genérica de valor).
  for (const c of decimais) {
    if (c < "0" || c > "9") {
      return false;
    }
  }
  return decimais.length > 2;
}

/**
 * `true` sse o valor canônico `"[-]INTEIRO.DD"` é estritamente positivo.
 * STRING-BASED: negativo (`-`) ou todos os dígitos zero → não positivo.
 */
function ehPositivo(canonico: string): boolean {
  if (canonico.startsWith("-")) {
    return false;
  }
  for (const c of canonico) {
    if (c >= "1" && c <= "9") {
      return true;
    }
  }
  return false;
}

/** Move o foco para o primeiro campo inválido (ordem: valor → data → cnpj). */
function focarPrimeiroErro(erros: ErrosCampo): void {
  const id =
    erros.valor !== null
      ? "receita-valor"
      : erros.data !== null
        ? "receita-data"
        : erros.cnpj !== null
          ? "receita-cnpj"
          : null;
  if (id !== null) {
    document.getElementById(id)?.focus();
  }
}

/**
 * Traduz o erro de uma mutação de receita numa mensagem AMIGÁVEL e estável,
 * mapeando a `categoria` tipada do `ErroHttp` (NUNCA ecoa status cru nem corpo do
 * servidor) — mesmo padrão de `lib/export/useExportExtrato`. Erro não-`ErroHttp`
 * → fallback genérico.
 *
 * NÃO é exportado (react-refresh/only-export-components): o mapeamento é
 * exercitado pela suíte através do banner renderizado (todas as categorias).
 */
function mensagemAmigavel(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    switch (erro.categoria) {
      case CategoriaErro.ModuloIndisponivel:
        return "Lançamento de receita indisponível no momento.";
      case CategoriaErro.NaoEncontrado:
        return "Empresa não encontrada.";
      case CategoriaErro.SemPermissao:
        return "Você não tem permissão para lançar receitas.";
      case CategoriaErro.SessaoExpirada:
        return "Sessão expirada. Faça login novamente.";
      case CategoriaErro.RequisicaoInvalida:
        return "Não foi possível lançar a receita. Verifique os dados.";
      default:
        return "Não foi possível lançar a receita. Tente novamente.";
    }
  }
  return "Não foi possível lançar a receita. Tente novamente.";
}
